import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Truck, CheckCircle2, MapPin, AlertTriangle, Timer, CalendarClock, Package, User, TrendingUp } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import "leaflet.heat";
import { HistoricoSemanalChart } from "@/components/logistica/HistoricoSemanalChart";

function StatCard({ label, value, icon: Icon, color, tid, hint }) {
  return (
    <Card data-testid={tid}>
      <CardContent className="p-4 flex items-center justify-between">
        <div>
          <div className="text-xs text-muted-foreground uppercase tracking-wide">{label}</div>
          <div className="text-2xl md:text-3xl font-extrabold mt-1">{value}</div>
          {hint && <div className="text-[10px] text-muted-foreground mt-1">{hint}</div>}
        </div>
        <div className={`${color} p-3 rounded-xl text-white`}><Icon className="w-5 h-5" /></div>
      </CardContent>
    </Card>
  );
}

function HeatMap({ points }) {
  const mapRef = useRef(null);
  const containerRef = useRef(null);
  const heatRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const center = points.length ? [points[0].lat, points[0].lng] : [-16.3287, -48.9530]; // Anápolis GO
    const map = L.map(containerRef.current, { zoomControl: true }).setView(center, 12);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "© OpenStreetMap contributors", maxZoom: 19,
    }).addTo(map);
    mapRef.current = map;
    return () => { map.remove(); mapRef.current = null; };
  }, []); // eslint-disable-line

  useEffect(() => {
    const map = mapRef.current; if (!map) return;
    if (heatRef.current) { map.removeLayer(heatRef.current); heatRef.current = null; }
    if (!points.length) return;
    const data = points.map((p) => [p.lat, p.lng, 0.7]);
    heatRef.current = L.heatLayer(data, { radius: 25, blur: 20, maxZoom: 15,
      gradient: { 0.2: "#3B82F6", 0.4: "#10B981", 0.6: "#F59E0B", 0.8: "#F97316", 1.0: "#DC2626" } }).addTo(map);
    // Ajusta bounds
    if (points.length > 1) {
      const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));
      map.fitBounds(bounds, { padding: [30, 30] });
    } else {
      map.setView([points[0].lat, points[0].lng], 15);
    }
  }, [points]);

  return <div ref={containerRef} data-testid="heatmap-container" style={{ height: 420, width: "100%", borderRadius: 12, overflow: "hidden" }}/>;
}

export default function Logistica() {
  const { currentEmpresaId } = useAuth();
  const [data, setData] = useState(null);
  const [range, setRange] = useState({ start: "", end: "" });

  useEffect(() => {
    const p = new URLSearchParams();
    if (range.start) p.set("start", range.start);
    if (range.end) p.set("end", range.end);
    const url = `/dashboard/logistica?${p.toString()}`;
    const load = () => api.get(url).then((r) => setData(r.data)).catch((e) => {
      console.error("Falha ao carregar dashboard logístico", e);
      setData({ error: true });
    });
    load();
    // Auto-refresh a cada 20s (mesma frequência de Entregas) para refletir mudanças
    // de status feitas pelo entregador no app mobile em outra sessão/dispositivo.
    const t = setInterval(load, 20000);
    // Refresh imediato quando algum status é alterado dentro DESTA sessão (mesmo browser).
    const onChanged = () => load();
    window.addEventListener("entregas-changed", onChanged);
    return () => { clearInterval(t); window.removeEventListener("entregas-changed", onChanged); };
    // `load` é criada dentro do effect, então não precisa entrar em deps. Reload quando
    // período ou empresa muda; polling e listener cuidam do resto.
  }, [range.start, range.end, currentEmpresaId]);

  const cards = useMemo(() => {
    if (!data || data.error) return [];
    const c = data.counts || {};
    return [
      { label: "Aguardando", value: c.aguardando || 0, icon: CalendarClock, color: "bg-orange-500", tid: "log-aguardando" },
      { label: "Em Rota", value: c.em_transito || 0, icon: Truck, color: "bg-blue-500", tid: "log-em-rota" },
      { label: "Concluídas", value: c.concluida || 0, icon: CheckCircle2, color: "bg-emerald-500", tid: "log-concluidas" },
      { label: "Remarcadas", value: c.remarcar || 0, icon: CalendarClock, color: "bg-amber-500", tid: "log-remarcadas",
        hint: "Entregas adiadas pelo entregador" },
      { label: "Canceladas", value: c.cancelada || 0, icon: AlertTriangle, color: "bg-rose-500", tid: "log-canceladas",
        hint: "Entregas canceladas com motivo" },
      { label: "Tempo médio confirmação", value: data.tempo_medio_confirmacao_min ? `${data.tempo_medio_confirmacao_min} min` : "—", icon: Timer, color: "bg-indigo-600", tid: "log-tempo",
        hint: `Baseado em ${data.total_concluidas} concluídas` },
      { label: "Pontos GPS Capturados", value: `${data.total_com_gps}/${data.total_concluidas}`, icon: MapPin, color: "bg-purple-600", tid: "log-gps-count",
        hint: data.total_concluidas ? `${Math.round((data.total_com_gps / data.total_concluidas) * 100)}% de cobertura` : "" },
      { label: "Divergências", value: (data.alertas || []).length, icon: AlertTriangle, color: "bg-rose-600", tid: "log-alertas" },
    ];
  }, [data]);

  return (
    <div className="space-y-6" data-testid="page-logistica">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold flex items-center gap-3">
            <Package className="w-8 h-8 text-orange-600"/>Dashboard Logístico
          </h1>
          <p className="text-muted-foreground">Rastreamento GPS · tempos · divergências</p>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base">Período</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div><Label className="text-xs">De</Label>
            <Input type="date" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} data-testid="log-start"/></div>
          <div><Label className="text-xs">Até</Label>
            <Input type="date" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} data-testid="log-end"/></div>
          <div className="flex items-end col-span-2 md:col-span-2">
            <Button variant="ghost" onClick={() => setRange({ start: "", end: "" })}>Limpar período</Button>
          </div>
        </CardContent>
      </Card>

      {!data ? <div className="p-8 text-muted-foreground">Carregando...</div> : data.error ? <div className="p-8 text-rose-600">Erro ao carregar dados</div> : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {cards.map((c) => <StatCard key={c.label} {...c} />)}
          </div>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2"><Timer className="w-5 h-5 text-indigo-600"/>Tempo Médio de Confirmação por Entregador</CardTitle>
              <span className="text-xs text-muted-foreground">{(data.tempo_por_entregador || []).length} entregador(es)</span>
            </CardHeader>
            <CardContent>
              {!(data.tempo_por_entregador || []).length ? (
                <div className="p-6 text-center text-muted-foreground bg-slate-50 rounded-lg">
                  <Timer className="w-8 h-8 mx-auto mb-2 text-slate-400"/>
                  Nenhuma entrega concluída com hora de saída + chegada registrada ainda no período
                </div>
              ) : (
                <>
                  <div className="text-xs text-muted-foreground mb-2">
                    ⚡ Ordenado do mais rápido ao mais lento · <b>tempo = hr_chegada − hr_saida</b>
                  </div>
                  <Table data-testid="table-tempo-por-entregador">
                    <TableHeader><TableRow>
                      <TableHead>Entregador</TableHead>
                      <TableHead className="text-center">Média (min)</TableHead>
                      <TableHead className="text-center">Mediana</TableHead>
                      <TableHead className="text-center">Min</TableHead>
                      <TableHead className="text-center">Máx</TableHead>
                      <TableHead className="text-center">Entregas</TableHead>
                      <TableHead>Comparativo</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {(() => {
                        const list = data.tempo_por_entregador;
                        const maxTempo = Math.max(...list.map((x) => x.tempo_medio_min), 1);
                        return list.map((e, idx) => {
                          const pctBar = Math.round((e.tempo_medio_min / maxTempo) * 100);
                          const barColor = idx === 0 ? "bg-emerald-500" : idx === list.length - 1 && list.length > 1 ? "bg-rose-500" : "bg-indigo-400";
                          return (
                            <TableRow key={e.entregador_id} className={idx === 0 ? "bg-emerald-50/40" : ""} data-testid={`tempo-ent-${e.entregador_id}`}>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  {idx === 0 && <TrendingUp className="w-4 h-4 text-emerald-600" title="Mais eficiente" />}
                                  <User className="w-4 h-4 text-slate-400" />
                                  <b>{e.nome}</b>
                                </div>
                              </TableCell>
                              <TableCell className="text-center font-extrabold text-lg text-indigo-700">{e.tempo_medio_min}</TableCell>
                              <TableCell className="text-center font-mono text-sm">{e.mediana_min}</TableCell>
                              <TableCell className="text-center font-mono text-xs text-emerald-700">{e.min_min}</TableCell>
                              <TableCell className="text-center font-mono text-xs text-rose-700">{e.max_min}</TableCell>
                              <TableCell className="text-center">
                                <span className="inline-flex items-center px-2 py-0.5 rounded bg-slate-100 text-slate-700 text-xs font-semibold">{e.n}</span>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden min-w-[80px]">
                                    <div className={`h-full ${barColor}`} style={{ width: `${pctBar}%` }} />
                                  </div>
                                  <span className="text-[10px] font-mono w-14 text-right text-muted-foreground">{e.tempo_medio_min} min</span>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        });
                      })()}
                    </TableBody>
                  </Table>
                </>
              )}
            </CardContent>
          </Card>

          <HistoricoSemanalChart historico={data.historico_semanal} />

          {/* Tempo de Espera — Aguardando Conferência (fev/2026) */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-violet-700">
                <Timer className="w-5 h-5"/>Tempo de Espera por Entregador — Aguardando Conferência
              </CardTitle>
              <span className="text-xs text-muted-foreground">{(data.espera_conferencia_por_entregador || []).length} entregador(es)</span>
            </CardHeader>
            <CardContent>
              {!(data.espera_conferencia_por_entregador || []).length ? (
                <div className="p-6 text-center text-muted-foreground bg-violet-50/40 rounded-lg" data-testid="espera-conf-empty">
                  <Timer className="w-8 h-8 mx-auto mb-2 text-violet-400"/>
                  Nenhuma entrega aguardando conferência no período
                </div>
              ) : (
                <>
                  <div className="text-xs text-muted-foreground mb-2">
                    ⏱ Ordenado pela maior espera · <b>tempo desde a atribuição do entregador até a confirmação de recebimento</b>
                  </div>
                  <Table data-testid="table-espera-conferencia">
                    <TableHeader><TableRow>
                      <TableHead>Entregador</TableHead>
                      <TableHead className="text-center">Aguardando</TableHead>
                      <TableHead className="text-center">Espera Média</TableHead>
                      <TableHead className="text-center">Maior Espera</TableHead>
                      <TableHead className="text-center">Menor Espera</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {(data.espera_conferencia_por_entregador || []).map((e) => {
                        const alertMax = e.espera_max_min > 15;
                        const rowCls = alertMax ? "bg-rose-50/50" : "";
                        const maxCls = alertMax ? "text-rose-700 font-extrabold" : "text-violet-700 font-bold";
                        return (
                          <TableRow key={e.entregador_id || "__none__"} className={rowCls} data-testid={`espera-ent-${e.entregador_id || "none"}`}>
                            <TableCell className="font-semibold">
                              <div className="flex items-center gap-2">
                                <User className="w-4 h-4 text-slate-400" />
                                {e.nome}
                                {alertMax && (
                                  <span className="text-[10px] font-bold text-rose-700 bg-rose-100 border border-rose-300 rounded-full px-1.5 py-0.5 uppercase animate-pulse">
                                    Urgente
                                  </span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-center">
                              <span className="inline-flex items-center px-2 py-0.5 rounded bg-violet-100 text-violet-800 text-sm font-bold">
                                {e.qtd}
                              </span>
                            </TableCell>
                            <TableCell className="text-center font-mono text-sm">{e.espera_media_min} min</TableCell>
                            <TableCell className={`text-center text-lg ${maxCls}`}>{e.espera_max_min} min</TableCell>
                            <TableCell className="text-center font-mono text-sm">{e.espera_min_min} min</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2"><MapPin className="w-5 h-5 text-orange-600"/>Mapa de Calor - Entregas Finalizadas</CardTitle>
              <span className="text-xs text-muted-foreground">{data.total_com_gps} pontos GPS</span>
            </CardHeader>
            <CardContent>
              {data.total_com_gps > 0 ? <HeatMap points={data.gps_points} /> : (
                <div className="p-8 text-center text-muted-foreground bg-slate-50 rounded-lg">
                  Nenhum ponto GPS registrado ainda. Os pontos aparecem aqui conforme os entregadores concluem entregas com GPS.
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-rose-700"><AlertTriangle className="w-5 h-5"/>Alertas de Divergência</CardTitle>
              <span className="text-xs text-muted-foreground">{data.alertas.length} alerta(s)</span>
            </CardHeader>
            <CardContent>
              {data.alertas.length === 0 ? (
                <div className="p-6 text-center text-emerald-700 bg-emerald-50 rounded-lg">
                  <CheckCircle2 className="w-8 h-8 mx-auto mb-2"/>Nenhuma divergência encontrada
                </div>
              ) : (
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>Nº Pedido</TableHead><TableHead>Cliente</TableHead>
                    <TableHead>Entregador</TableHead><TableHead>Descrição</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {data.alertas.map((a) => (
                      <TableRow key={a.id} className="bg-rose-50/40" data-testid={`alerta-${a.id}`}>
                        <TableCell className="mono text-xs">{a.numero_pedido || "—"}</TableCell>
                        <TableCell className="text-sm">{a.cliente || "—"}</TableCell>
                        <TableCell className="text-sm">{a.entregador}</TableCell>
                        <TableCell className="text-xs">{a.descricao}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
